import { useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BottomActionButton } from "@components";

const StyledDialog = styled(Dialog)({
  ".MuiDialog-paper": {
    maxWidth: "440px",
    borderRadius: "10px",
  },
});

const DialogContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  padding: "24px",
});

const HeaderSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const TitleRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
});

const Title = styled(DialogTitle)(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 600,
  color: theme.palette.text.primary,
  padding: 0,
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 400,
  color: theme.palette.text.primary,
  lineHeight: "23px",
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.icon.primary,
  padding: 0,
}));

const ButtonContainer = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
});

const SaveButton = styled(BottomActionButton)({
  width: "auto",
  paddingLeft: "32px",
  paddingRight: "32px",
});

interface SaveAsTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (templateName: string) => void;
  loading?: boolean;
}

export const SaveAsTemplateModal = ({
  isOpen,
  onClose,
  onSave,
  loading,
}: SaveAsTemplateModalProps) => {
  const muiTheme = useTheme();
  const [templateName, setTemplateName] = useState("");

  const handleClose = () => {
    setTemplateName("");
    onClose();
  };

  const handleSave = () => {
    if (!templateName.trim()) return;
    onSave(templateName.trim());
  };

  return (
    <StyledDialog open={isOpen} onClose={handleClose}>
      <DialogContainer>
        <HeaderSection>
          <TitleRow>
            <Title>¿Deseas guardar como plantilla?</Title>
            <CloseButton onClick={handleClose}>
              <CloseIcon />
            </CloseButton>
          </TitleRow>
          <Subtitle>
            Guarda esta receta como plantilla para repetirlo con otros
            pacientes.
          </Subtitle>
        </HeaderSection>

        <Box display="flex" flexDirection="column" gap="4px">
          <Typography
            fontSize="16px"
            fontWeight={400}
            color={muiTheme.palette.text.primary}
          >
            Nombre de la plantilla
          </Typography>
          <TextField
            fullWidth
            autoFocus
            placeholder="Rinitis alérgica"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            size="small"
          />
        </Box>

        <ButtonContainer>
          <SaveButton
            onClick={handleSave}
            disabled={!templateName.trim() || loading}
          >
            {loading ? "Guardando..." : "Guardar"}
          </SaveButton>
        </ButtonContainer>
      </DialogContainer>
    </StyledDialog>
  );
};
